import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";

interface WashData {
  id: number;
  vehicle?: {
    regNo: string;
    customer?: { name: string; phone: string };
  };
  masterServices?: { serviceName: string }[];
  actualAmount?: number | null;
  washStatus?: string;
  paymentStatus?: string;
}

interface RevertWashDialogProps {
  open: boolean;
  onClose: () => void;
  wash: WashData | null;
  onSuccess: () => void;
}

const statusColors: Record<string, { bg: string; color: string }> = {
  Completed: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981" },
  Pending: { bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" },
  "In Progress": { bg: "rgba(37, 99, 235, 0.1)", color: "#2563eb" },
  Cancelled: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444" },
};

export default function RevertWashDialog({
  open,
  onClose,
  wash,
  onSuccess,
}: RevertWashDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleRevert = async () => {
    if (!wash) return;
    setSubmitting(true);
    try {
      await request.post("/api/revertWash", { washId: wash.id });
      toast.success("Wash reverted to pending!");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to revert wash");
    } finally {
      setSubmitting(false);
    }
  };

  if (!wash) return null;

  const washScheme =
    statusColors[wash.washStatus || "Pending"] || statusColors.Pending;
  const paymentScheme =
    statusColors[wash.paymentStatus || "Pending"] || statusColors.Pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <UndoIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box
          sx={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}
        >
          Revert Wash
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            <Info label="Reg No" value={wash.vehicle?.regNo || "-"} />
            <Info
              label="Customer"
              value={wash.vehicle?.customer?.name || "-"}
            />
          </Box>

          <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

          <Info
            label="Services"
            value={
              wash.masterServices?.map((s) => s.serviceName).join(", ") || "-"
            }
          />

          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <StatusBox
              label="Wash Status"
              status={wash.washStatus || "Pending"}
              scheme={washScheme}
            />
            <StatusBox
              label="Payment"
              status={wash.paymentStatus || "Pending"}
              scheme={paymentScheme}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: "rgba(245, 158, 11, 0.1)",
              border: "1px solid rgba(245, 158, 11, 0.2)",
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "rgba(245, 158, 11, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16, color: "#d97706" }}
              >
                info
              </span>
            </Box>
            <Box
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#92400e",
                lineHeight: 1.4,
              }}
            >
              This wash will be moved back to Pending and the completed date &
              time will be cleared.
              {wash.paymentStatus === "Completed"
                ? " Note: the payment recorded for this wash stays as it is."
                : ""}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: "rgba(0,0,0,0.12)",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRevert}
          variant="contained"
          size="small"
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <UndoIcon />
            )
          }
          sx={{
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": { background: "linear-gradient(135deg, #d97706, #b45309)" },
          }}
        >
          {submitting ? "Reverting..." : "Confirm & Revert"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Box
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          mb: 0.15,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontSize: "0.82rem",
          fontWeight: 600,
          color: "#1c1b1f",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function StatusBox({
  label,
  status,
  scheme,
}: {
  label: string;
  status: string;
  scheme: { bg: string; color: string };
}) {
  return (
    <Box>
      <Box
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          mb: 0.3,
        }}
      >
        {label}
      </Box>
      <Chip
        label={status}
        size="small"
        sx={{
          background: scheme.bg,
          color: scheme.color,
          fontWeight: 700,
          fontSize: "0.7rem",
          height: 22,
        }}
      />
    </Box>
  );
}
